import React, { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import MainSlider from '../components/home/MainSlider';
import Counter from '../components/home/Counter';

const Section = styled.section`
  max-width: ${props => props.theme.layout.content};
  margin: 0 auto;
  padding: clamp(70px, 9vw, 130px) ${props => props.theme.layout.gutter};

  @media (max-width: 768px) {
    padding: 60px ${props => props.theme.layout.gutterMobile};
  }
`;

/* 제목 위에 붙는 작은 라벨. 옆으로 뻗는 선이 시선을 제목으로 끌어준다. */
const Eyebrow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 22px;
  color: ${props => props.theme.colors.accent};
  font-size: ${props => props.theme.type.eyebrow};
  font-weight: ${props => props.theme.weight.heavy};
  letter-spacing: ${props => props.theme.tracking.eyebrow};

  &::after {
    flex: 1;
    height: 1px;
    background: ${props => props.theme.colors.border};
    content: '';
  }
`;

const SectionTitle = styled.h3`
  max-width: 900px;
  font-size: ${props => props.theme.type.title};
  font-weight: ${props => props.theme.weight.heavy};
  line-height: 1.14;
  letter-spacing: ${props => props.theme.tracking.title};
`;

/*
 * 6칸 격자 위에서 앞의 2개만 3칸씩 차지해 크게, 나머지 6개는 2칸씩 차지한다.
 * 결과적으로 위는 2단, 아래는 3단으로 흐름에 강약이 생긴다.
 */
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: clamp(14px, 1.6vw, 24px);
  margin-top: clamp(45px, 5vw, 75px);

  > *:nth-child(-n + 2) {
    grid-column: span 3;
  }

  > *:nth-child(n + 3) {
    grid-column: span 2;
  }

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);

    > *:nth-child(-n + 2),
    > *:nth-child(n + 3) {
      grid-column: span 1;
    }
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

/* 카드 전체가 링크. a 태그라 새 탭 열기·우클릭이 정상 동작한다. */
const Card = styled(Link)`
  position: relative;
  display: block;
  overflow: hidden;
  aspect-ratio: ${props => (props.$featured ? '16 / 10' : '4 / 3')};
  background: ${props => props.theme.colors.primary};

  @media (max-width: 560px) {
    aspect-ratio: 16 / 10;
  }
`;

const Photo = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.6s cubic-bezier(0.2, 0.8, 0.2, 1);

  ${Card}:hover & {
    transform: scale(1.06);
  }
`;

/* 사진이 아직 없을 때 자리를 지키는 블록. 브랜드 블루 위 사선 무늬. */
const Placeholder = styled.div`
  width: 100%;
  height: 100%;
  background: repeating-linear-gradient(
    -45deg,
    ${props => props.theme.colors.primary},
    ${props => props.theme.colors.primary} 12px,
    ${props => props.theme.colors.secondary} 12px,
    ${props => props.theme.colors.secondary} 24px
  );
`;

/*
 * 사진 위에 글자를 얹으려면 반드시 어둡게 깔아야 한다.
 * 밝은 하늘이 찍힌 사진에서도 흰 글씨가 읽히도록 아래쪽을 진하게 만든다.
 */
const Scrim = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    rgba(12, 20, 40, 0.88) 0%,
    rgba(12, 20, 40, 0.45) 42%,
    rgba(12, 20, 40, 0.08) 100%
  );
  transition: background 0.35s ease;

  ${Card}:hover & {
    background: linear-gradient(
      to top,
      rgba(12, 20, 40, 0.92) 0%,
      rgba(12, 20, 40, 0.6) 55%,
      rgba(12, 20, 40, 0.25) 100%
    );
  }
`;

const Caption = styled.div`
  position: absolute;
  right: clamp(16px, 1.8vw, 28px);
  bottom: clamp(16px, 1.8vw, 26px);
  left: clamp(16px, 1.8vw, 28px);
  color: ${props => props.theme.colors.white};

  h4 {
    font-size: ${props => props.$featured ? props.theme.type.subtitle : props.theme.type.cardTitle};
    font-weight: ${props => props.theme.weight.heavy};
    line-height: 1.25;
    letter-spacing: ${props => props.theme.tracking.card};
  }

  p {
    margin-top: 8px;
    color: rgba(255, 255, 255, 0.78);
    font-size: ${props => props.theme.type.small};
    font-weight: ${props => props.theme.weight.body};
    line-height: 1.6;
  }
`;

/* 클릭 가능하다는 신호. 호버 시 오렌지로 채워진다. */
const Arrow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  margin-top: 14px;
  color: ${props => props.theme.colors.white};
  font-size: 12px;
  font-weight: ${props => props.theme.weight.bold};
  letter-spacing: ${props => props.theme.tracking.eyebrow};
  opacity: 0;
  transform: translateY(6px);
  transition: opacity 0.3s ease, transform 0.3s ease, color 0.3s ease;

  ${Card}:hover & {
    color: ${props => props.theme.colors.accent};
    opacity: 1;
    transform: translateY(0);
  }

  /* 손가락 입력 기기는 호버가 없으므로 항상 보여준다 */
  @media (hover: none) {
    opacity: 1;
    transform: none;
  }
`;

/*
 * 시공사진 파일명은 경로 슬러그와 맞춘다. (예: /business/interior -> business_interior.jpg)
 * public/images/ 에 해당 파일을 넣으면 자동으로 표시되고, 없으면 무늬 블록으로 대체된다.
 */
const businessItems = [
  { path: '/business/interior', img: '/images/business_interior.jpg', title: '실내건축', desc: '감각적인 디자인과 완벽한 시공' },
  { path: '/business/landscape', img: '/images/business_landscape.jpg', title: '조경식재', desc: '도심 속 쾌적한 녹색 환경 조성' },
  { path: '/business/metal', img: '/images/business_metal.jpg', title: '금속구조물·지붕', desc: '지붕판금·건축물조립 전문 시공' },
  { path: '/business/finishing', img: '/images/business_finishing.jpg', title: '도장·방수·석공사', desc: '습식·방수공사 전문 시공' },
  { path: '/business/pavement', img: '/images/business_pavement.jpg', title: '지반조성·포장', desc: '정밀 다짐 기반 포장공사 전문' },
  { path: '/business/management', img: '/images/business_management.jpg', title: '건물종합관리', desc: '전문적인 시설 및 위생 관리' },
  { path: '/business/tank', img: '/images/business_tank.jpg', title: '물탱크청소', desc: '저수조 청소 및 수질 위생 관리' },
  { path: '/business/disinfection', img: '/images/business_disinfection.jpg', title: '소독업', desc: '방역·방제 전문 소독 서비스' },
];

const BusinessCard = ({ item, featured }) => {
  const [error, setError] = useState(false);

  return (
    <Card to={item.path} $featured={featured} aria-label={`${item.title} 자세히 보기`}>
      {error ? (
        <Placeholder />
      ) : (
        <Photo src={item.img} alt={`${item.title} 시공 사진`} loading="lazy" onError={() => setError(true)} />
      )}
      <Scrim />
      <Caption $featured={featured}>
        <h4>{item.title}</h4>
        <p>{item.desc}</p>
        <Arrow>VIEW <span aria-hidden="true">→</span></Arrow>
      </Caption>
    </Card>
  );
};

const Home = () => {
  return (
    <>
      <MainSlider />
      <Counter />

      <Section>
        <Eyebrow>BUSINESS</Eyebrow>
        <SectionTitle>주요 사업영역</SectionTitle>
        <Grid>
          {businessItems.map((item, index) => (
            <BusinessCard key={item.path} item={item} featured={index < 2} />
          ))}
        </Grid>
      </Section>
    </>
  );
};

export default Home;
